/** Seeds the realtime alert slice with a facility's open alerts (rows + server total) for every facility page, refreshing every 60 s. */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch } from '@/store';
import { getAlertsPage } from '@/api/alerts';
import { seedAlerts } from '@/store/slices/realtimeSlice';
import { normalizeSeededAlert, type RestAlertRow } from '@/utils/alertSeed';
import type { Alert } from '@/types';

export const OPEN_ALERT_SEED_LIMIT = 100;
export const OPEN_ALERT_SEED_INTERVAL_MS = 60_000;

/**
 * Mounted once in FacilityLayout so the Header bell, Sidebar badge, Dashboard KPI, and
 * Command Center all see the same open-alert state regardless of which page loaded
 * first (the stream only delivers alert_update events that occur after connect).
 * Pass undefined to disable (no facility resolved, or unauthenticated).
 */
export function useOpenAlertsSeed(facilityId: string | undefined): void {
  const dispatch = useAppDispatch();
  const query = useQuery({
    queryKey: ['open-alerts-seed', facilityId],
    queryFn: () =>
      getAlertsPage({ facilityId: facilityId!, status: 'active', limit: OPEN_ALERT_SEED_LIMIT }),
    enabled: Boolean(facilityId),
    staleTime: OPEN_ALERT_SEED_INTERVAL_MS,
    refetchInterval: OPEN_ALERT_SEED_INTERVAL_MS,
  });

  const page = query.data;
  const fetchedAt = query.dataUpdatedAt;

  useEffect(() => {
    // react-query only exposes data for the CURRENT key, so a response for a previous
    // facility can never be dispatched under the new facilityId.
    if (!facilityId || !page) return;
    const alerts = page.alerts
      .map((row) => normalizeSeededAlert(row as RestAlertRow, facilityId))
      .filter((alert): alert is Alert => alert !== null);
    dispatch(
      seedAlerts({
        facilityId,
        alerts,
        total: page.total ?? alerts.length,
        // A short page is complete; so is a full page whose server total it already covers
        // (exactly OPEN_ALERT_SEED_LIMIT open alerts).
        complete:
          page.alerts.length < OPEN_ALERT_SEED_LIMIT ||
          (page.total != null && page.alerts.length >= page.total),
        fetchedAt: fetchedAt || Date.now(),
      }),
    );
  }, [facilityId, page, fetchedAt, dispatch]);
}
